import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface GroupTypeDto {
    [key: string]: any;
    id: number;
    nameFr: string;
    nameEn: string;
    region_Id: number;
}

export interface GroupTypeState {
    groupTypes: GroupTypeDto[];
    groupType: GroupTypeDto;
    gettingGroupTypes: boolean;
    gettingGroupType: boolean;
    archivingGroupType: boolean;
    savingGroupType: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

const blankGroupTypeDto: GroupTypeDto = {
    id: 0,
    nameFr: '',
    nameEn: '',
    region_Id: 0,
};

const initialState: GroupTypeState = {
    groupTypes: [],
    groupType: blankGroupTypeDto,
    gettingGroupTypes: false,
    gettingGroupType: false,
    archivingGroupType: false,
    savingGroupType: false,
    status: 'idle',
    error: undefined,
};

export const groupTypeSlice = createSlice({
    name: 'groupTypes',
    initialState,
    reducers: {
        clearGroupTypes: (state) => {
            state.groupTypes = [];
            state.groupType = blankGroupTypeDto;
        },
        updateGroupType: (state, param) => {
            const { payload } = param;
            state.groupType = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getGroupTypes.pending, (state, action) => {
                state.gettingGroupTypes = true;
                state.status = 'loading';
            })
            .addCase(getGroupTypes.fulfilled, (state, action) => {
                state.gettingGroupTypes = false;
                state.groupTypes = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getGroupTypes.rejected, (state, action) => {
                state.gettingGroupTypes = false;
                state.groupTypes = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getGroupType.pending, (state, action) => {
                state.gettingGroupType = true;
                state.status = 'loading';
            })
            .addCase(getGroupType.fulfilled, (state, action) => {
                state.gettingGroupType = false;
                state.groupType = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getGroupType.rejected, (state, action) => {
                state.gettingGroupType = false;
                state.groupType = blankGroupTypeDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveGroupType.pending, (state, action) => {
                state.savingGroupType = true;
                state.status = 'loading';
            })
            .addCase(saveGroupType.fulfilled, (state, action) => {
                state.savingGroupType = false;
                state.status = 'succeeded';
            })
            .addCase(archiveGroupType.fulfilled, (state, action) => {
                state.archivingGroupType = false;
                state.groupTypes = state.groupTypes.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveGroupType.rejected, (state, action) => {
                state.archivingGroupType = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearGroupTypes } = groupTypeSlice.actions;
export const { updateGroupType } = groupTypeSlice.actions;

export const getGroupTypes = createAsyncThunk('groupTypes/getGroupTypes', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/group-types`);

    return response.data;
});

export const getGroupType = createAsyncThunk('groupTypes/getGroupType', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/group-types/${id}`);

    return response.data;
});

export const archiveGroupType = createAsyncThunk('groupTypes/archiveGroupType', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/group-types/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedGroupType'),
        })
    );
    return response.data;
});

export const saveGroupType = createAsyncThunk('groupTypes/saveGroupType', async (groupTypeToSave: GroupTypeDto, thunkAPI) => {
    if (groupTypeToSave.id === 0) {
        const response = await axios.post(`/api/synergy/group-types/create`, groupTypeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('createGroupType'),
            })
        );
        return response.data;
    } else {
        const response = await axios.put(`/api/synergy/group-types/put`, groupTypeToSave);
        thunkAPI.dispatch(
            showMessage({
                type: 'info',
                message: getAppText('saveGroupType'),
            })
        );
        return response.data;
    }
});
